// La bourse fictive : un marché « monde » inventé, cinq fonds et six actions d'entreprises imaginaires,
// chacun avec sa façon de bouger. Aucune donnée ni entreprise réelle.
import type { TextKey } from './fr';
import type { AssetId, MarketRegime, MarketState } from '../engine/state';

/** Les phases du marché : ce qu'il gagne ou perd en moyenne par an, et combien il varie. */
export const REGIMES: Record<MarketRegime, { drift: number; vol: number }> = {
  croissance: { drift: 0.08, vol: 0.13 },
  bulle: { drift: 0.28, vol: 0.15 },
  krach: { drift: -0.42, vol: 0.22 },
  reprise: { drift: 0.2, vol: 0.18 },
  // La première baisse garantie : environ 10 à 15 % en trois mois.
  correction: { drift: -0.5, vol: 0.08 },
};

/** Les changements de phase, chaque mois. */
export const TRANSITIONS = {
  /** En croissance : chance par mois qu'une bulle commence, ou qu'un krach arrive sans prévenir. */
  bubbleChance: 0.012,
  suddenCrashChance: 0.004,
  /** Une bulle finit en krach : chance par mois, et au plus tard au bout de trente mois. */
  burstChance: 0.06,
  maxBubbleMonths: 30,
  /** Durées, en mois, d'un krach, d'une reprise, et de la première baisse. */
  crashMonths: [5, 12] as [number, number],
  recoveryMonths: [12, 30] as [number, number],
  correctionMonths: 3,
  /** La première baisse arrive un à deux ans après le premier achat. */
  firstDropDays: [365, 730] as [number, number],
};

export interface AssetDef {
  id: AssetId;
  kind: 'fonds' | 'action';
  name: TextKey;
  /** Prix d'une part au début de la partie. */
  startPrice: number;
  /** Frais de gestion d'un fonds, par an : ils sont pris sur sa valeur, jour après jour. */
  fees: number;
  /** La part des mouvements du marché qu'elle suit (1 : autant que lui). */
  beta: number;
  /** Ce qu'elle gagne ou perd par an, en plus de ce que lui donne le marché. */
  drift: number;
  /**
   * Un fonds à effet de levier : chaque jour, ce multiple de la variation du marché (et non de son logarithme).
   * Le levier se recalcule tous les jours : le fonds s'use quand le marché fait du yo-yo.
   */
  leverage?: number;
  /** Ses variations propres, par an. */
  vol: number;
  /** Un dividende versé chaque année en mai, en part du prix. */
  dividend?: number;
  /** Un cycle économique de plusieurs années, en plus du marché. */
  cycle?: { amplitude: number; years: number };
  /** De rares grosses nouvelles : la chance par mois, la hausse et la baisse possibles. */
  news?: { chancePerMonth: number; up: number; down: number };
  /** La présentation détaillée, sur sa carte au Marché. */
  about: TextKey;
  /** Le PER habituel : le prix divisé par le bénéfice annuel, quand elle n'est ni chère ni bradée. */
  per?: number;
  /** L'endettement habituel : les dettes rapportées à ce que l'entreprise possède en propre. */
  debt?: number;
  /** Le nombre d'entreprises ou d'obligations dans un fonds. */
  lines?: number;
  /** Une entreprise qui ne gagne pas encore d'argent : les bénéfices arrivent avec une bonne nouvelle. */
  lossMaking?: boolean;
}

export const ASSETS: AssetDef[] = [
  // Les fonds : un fonds indiciel monde pas cher, le fonds « maison » de la banque, un fonds d'obligations.
  {
    id: 'monde',
    kind: 'fonds',
    name: 'asset.monde',
    about: 'asset.monde.about',
    startPrice: 100,
    fees: 0.002,
    beta: 1,
    drift: 0,
    vol: 0,
    per: 17,
    lines: 1500,
  },
  {
    id: 'maison',
    kind: 'fonds',
    name: 'asset.maison',
    about: 'asset.maison.about',
    startPrice: 100,
    fees: 0.018,
    beta: 1,
    drift: 0,
    vol: 0.02,
    per: 17,
    lines: 80,
  },
  {
    id: 'oblig',
    kind: 'fonds',
    name: 'asset.oblig',
    about: 'asset.oblig.about',
    startPrice: 50,
    fees: 0.003,
    beta: -0.05,
    drift: 0.036,
    vol: 0.05,
    lines: 350,
  },
  // Deux fonds pour apprendre à se méfier : le levier, et la mode du moment.
  {
    id: 'levier',
    kind: 'fonds',
    name: 'asset.levier',
    about: 'asset.levier.about',
    startPrice: 100,
    fees: 0.006,
    beta: 2,
    leverage: 2,
    // Le coût de l'argent emprunté pour doubler la mise.
    drift: -0.02,
    vol: 0,
    per: 17,
    lines: 1500,
  },
  {
    id: 'robotique',
    kind: 'fonds',
    name: 'asset.robotique',
    about: 'asset.robotique.about',
    startPrice: 50,
    fees: 0.0065,
    beta: 1.4,
    // La mode se paie : des actions chères, qui rapportent moins que leur risque ne le laisse espérer.
    drift: -0.03,
    vol: 0.1,
    per: 35,
    lines: 40,
  },
  // Les actions : une défensive qui verse un dividende, une valeur de croissance, une cyclique, une spéculative,
  // une fausse bonne affaire, et une excellente entreprise payée cher.
  {
    id: 'valdore',
    kind: 'action',
    name: 'asset.valdore',
    about: 'asset.valdore.about',
    startPrice: 60,
    fees: 0,
    beta: 0.5,
    drift: 0.035,
    vol: 0.1,
    dividend: 0.04,
    per: 16,
    debt: 0.45,
  },
  {
    id: 'pixelune',
    kind: 'action',
    name: 'asset.pixelune',
    about: 'asset.pixelune.about',
    startPrice: 120,
    fees: 0,
    beta: 1.5,
    drift: 0,
    vol: 0.3,
    per: 30,
    debt: 0.05,
  },
  {
    id: 'forges',
    kind: 'action',
    name: 'asset.forges',
    about: 'asset.forges.about',
    startPrice: 45,
    fees: 0,
    beta: 1.1,
    drift: 0,
    vol: 0.18,
    cycle: { amplitude: 0.18, years: 6 },
    dividend: 0.02,
    per: 11,
    debt: 0.9,
  },
  {
    id: 'biomyrte',
    kind: 'action',
    name: 'asset.biomyrte',
    about: 'asset.biomyrte.about',
    startPrice: 12,
    fees: 0,
    beta: 0.6,
    drift: 0,
    vol: 0.35,
    news: { chancePerMonth: 0.03, up: 0.6, down: 0.5 },
    per: 25,
    debt: 0,
    lossMaking: true,
  },
  {
    // Le piège des valeurs « pas chères » : un PER bas, un gros dividende, et des ventes qui reculent.
    id: 'morin',
    kind: 'action',
    name: 'asset.morin',
    about: 'asset.morin.about',
    startPrice: 24,
    fees: 0,
    beta: 0.9,
    drift: -0.05,
    vol: 0.2,
    // Des plans de relance qui font rebondir le cours, quelque temps.
    cycle: { amplitude: 0.12, years: 5 },
    dividend: 0.07,
    per: 6,
    debt: 1.2,
  },
  {
    // Une entreprise remarquable, mais payée cher : quand le marché doute, les actions chères chutent fort.
    id: 'aurele',
    kind: 'action',
    name: 'asset.aurele',
    about: 'asset.aurele.about',
    startPrice: 180,
    fees: 0,
    beta: 1.2,
    drift: 0.01,
    vol: 0.22,
    dividend: 0.015,
    per: 30,
    debt: 0.3,
  },
];

export const ASSET_IDS: AssetId[] = ASSETS.map((asset) => asset.id);

export function getAsset(id: AssetId): AssetDef {
  const asset = ASSETS.find((item) => item.id === id);
  if (!asset) throw new Error(`Placement inconnu : ${id}`);
  return asset;
}

/** Le marché au début de la partie : en croissance, aux prix de départ. */
export function startingMarket(seed: number): MarketState {
  const prices = Object.fromEntries(ASSETS.map((asset) => [asset.id, asset.startPrice])) as Record<AssetId, number>;
  const history = Object.fromEntries(ASSETS.map((asset) => [asset.id, [asset.startPrice]])) as Record<AssetId, number[]>;
  return {
    // Un tirage à part : le marché ne change pas les autres hasards de la partie.
    rngState: (Math.imul(seed | 0, 2654435761) ^ 0x5bd1e995) | 0,
    regime: 'croissance',
    regimeMonths: 0,
    monthsLeft: 0,
    lastDay: 0,
    lastMonth: 0,
    index: 100,
    indexPeak: 100,
    indexHistory: [100],
    prices,
    history,
    firstDropDay: null,
    firstDropDone: false,
    ema: { ...prices },
    emaHistory: Object.fromEntries(ASSETS.map((asset) => [asset.id, [asset.startPrice]])) as Record<AssetId, number[]>,
    indexEma: 100,
    profitable: {},
    eurosRate: 0.026,
    realEstate: 100,
    realEstateHistory: [100],
    realEstateSlump: 0,
  };
}
